import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ImagePlus, Loader2, Trash2, X, ZoomIn } from "lucide-react";
import { toast } from "react-toastify";
import {
  addImagesToChangeNote,
  deleteOrderChangeImage,
  fetchOrderChangeImageBlob,
  fetchOrderChangeNotes,
  saveOrderChange,
  updateOrderChangeNoteText,
} from "../../api/OrderChangeNoteAPIs";

const MAX_BYTES = 8 * 1024 * 1024;
const MAX_FILES = 8;
const ALLOWED_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

const extensionOf = (name) => {
  const dot = String(name || "").lastIndexOf(".");
  return dot >= 0 ? String(name).slice(dot).toLowerCase() : "";
};

const validateFiles = (fileList, existingCount) => {
  const accepted = [];
  const errors = [];
  Array.from(fileList || []).forEach((file) => {
    const ext = extensionOf(file.name);
    const typeOk = ALLOWED_TYPES.has(file.type) || [".jpg", ".jpeg", ".png", ".webp"].includes(ext);
    if (!typeOk) {
      errors.push(`${file.name}: الصيغة غير مدعومة. المسموح JPG و PNG و WebP.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      errors.push(`${file.name}: الحجم أكبر من 8 ميجابايت.`);
      return;
    }
    if (file.size <= 0) {
      errors.push(`${file.name}: الملف فارغ.`);
      return;
    }
    accepted.push(file);
  });
  if (existingCount + accepted.length > MAX_FILES) {
    return { accepted: [], errors: [`يمكن اختيار ${MAX_FILES} صور كحد أقصى في المرة الواحدة.`] };
  }
  return { accepted, errors };
};

const OrderChangesModal = ({ orderId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [text, setText] = useState("");
  const [pending, setPending] = useState([]);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const savingRef = useRef(false);
  const inputRef = useRef(null);
  const pendingRef = useRef([]);
  const savedUrlsRef = useRef([]);
  pendingRef.current = pending;

  const load = async (id) => {
    setLoading(true);
    setLoadError("");
    try {
      const payload = await fetchOrderChangeNotes(id);
      const blobUrls = [];
      const notes = await Promise.all(
        (payload.notes || []).map(async (note) => ({
          ...note,
          images: await Promise.all(
            (note.images || []).map(async (image) => {
              try {
                const blob = await fetchOrderChangeImageBlob(id, image.id);
                const url = URL.createObjectURL(blob);
                blobUrls.push(url);
                return { ...image, url };
              } catch {
                return { ...image, url: "", missing: true };
              }
            })
          ),
        }))
      );
      savedUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      savedUrlsRef.current = blobUrls;
      setData({ ...payload, notes });
    } catch (err) {
      setLoadError(err.message || "تعذر تحميل ملاحظات التغيير");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId) return undefined;
    setText("");
    setPending([]);
    setFormError("");
    setProgress(0);
    setEditingId(null);
    load(orderId);
    return () => {
      pendingRef.current.forEach((item) => URL.revokeObjectURL(item.url));
      savedUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      savedUrlsRef.current = [];
      pendingRef.current = [];
    };
  }, [orderId]);

  if (!orderId) return null;

  const addPending = (fileList) => {
    const { accepted, errors } = validateFiles(fileList, pending.length);
    if (errors.length) setFormError(errors.join(" "));
    else setFormError("");
    if (!accepted.length) return;
    setPending((current) => [
      ...current,
      ...accepted.map((file) => ({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        url: URL.createObjectURL(file),
      })),
    ]);
  };

  const removePending = (id) => {
    setPending((current) => {
      const target = current.find((item) => item.id === id);
      if (target) URL.revokeObjectURL(target.url);
      return current.filter((item) => item.id !== id);
    });
  };

  const handleSave = async () => {
    if (savingRef.current) return;
    const reason = text.trim();
    if (!reason && pending.length === 0) {
      setFormError("يجب إدخال سبب!");
      return;
    }
    if (!reason && data?.status !== "Changes") {
      setFormError("يجب إدخال سبب!");
      return;
    }

    savingRef.current = true;
    setSaving(true);
    setProgress(pending.length ? 1 : 0);
    setFormError("");
    try {
      await saveOrderChange({
        orderId,
        text: reason,
        files: pending.map((item) => item.file),
        applyStatus: Boolean(reason),
        onProgress: setProgress,
      });
      toast.success(reason ? "تم حفظ التغييرات" : "تم حفظ الصور المرجعية");
      await onSaved?.(orderId);
    } catch (err) {
      setFormError(err.message || "فشل حفظ التغييرات");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const saveEditedText = async (noteId) => {
    const next = editingText.trim();
    if (!next) {
      toast.error("يجب إدخال سبب!");
      return;
    }
    setSaving(true);
    try {
      const payload = await updateOrderChangeNoteText(orderId, noteId, next);
      setData((current) => ({ ...current, ...payload, notes: current.notes.map((note) => note.id === noteId ? { ...note, text: next } : note) }));
      setEditingId(null);
      toast.success("تم تحديث وصف التغييرات");
    } catch (err) {
      toast.error(err.message || "فشل تحديث النص");
    } finally {
      setSaving(false);
    }
  };

  const uploadToNote = async (noteId, fileList) => {
    const { accepted, errors } = validateFiles(fileList, 0);
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    if (!accepted.length) return;
    setSaving(true);
    setProgress(1);
    try {
      await addImagesToChangeNote({ orderId, noteId, files: accepted, onProgress: setProgress });
      toast.success("تمت إضافة الصور المرجعية");
      await load(orderId);
    } catch (err) {
      toast.error(err.message || "فشل رفع الصور");
    } finally {
      setSaving(false);
      setProgress(0);
    }
  };

  const removeSavedImage = async (imageId) => {
    if (!window.confirm("حذف هذه الصورة المرجعية؟")) return;
    setSaving(true);
    try {
      await deleteOrderChangeImage(orderId, imageId);
      toast.success("تم حذف الصورة");
      await load(orderId);
    } catch (err) {
      toast.error(err.message || "فشل حذف الصورة");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] bg-slate-900/55 flex items-end sm:items-center justify-center p-0 sm:p-4" dir="rtl">
      <div className="bg-white w-full sm:max-w-3xl max-h-[100vh] sm:max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl shadow-2xl">
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b px-4 sm:px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold text-purple-600">تغييرات الطلب</p>
            <h2 className="text-2xl font-black text-slate-900">#{orderId}</h2>
            <p className="text-sm text-slate-500 mt-1">صور مرجعية لتعليمات الطباعة والتغييرات، وليست صور المنتج.</p>
          </div>
          <button type="button" disabled={saving} onClick={onClose} className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-40" aria-label="إغلاق">
            <X size={20} />
          </button>
        </div>

        <div className="px-4 sm:px-6 py-4 space-y-5">
          {loading && <p className="text-slate-500">جاري تحميل الملاحظات والصور...</p>}
          {loadError && <p className="text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{loadError}</p>}

          {data?.legacyNote && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 px-4 py-3">
              <p className="text-xs font-bold text-amber-700 mb-1">الملاحظة الحالية على الطلب</p>
              <p className="whitespace-pre-wrap text-amber-950">{data.legacyNote}</p>
            </div>
          )}

          {(data?.notes || []).length > 0 && (
            <div className="space-y-3">
              <p className="font-bold text-slate-800">التغييرات المحفوظة</p>
              {data.notes.map((note) => (
                <article key={note.id} className="rounded-2xl border border-slate-200 p-3 space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-bold text-slate-500">محفوظة</span>
                    <button
                      type="button"
                      className="text-xs text-purple-700 font-bold"
                      onClick={() => {
                        setEditingId(note.id);
                        setEditingText(note.text || "");
                      }}
                    >
                      تعديل النص
                    </button>
                  </div>
                  {editingId === note.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingText}
                        onChange={(event) => setEditingText(event.target.value)}
                        rows={4}
                        className="w-full rounded-xl border border-slate-200 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-300"
                      />
                      <button type="button" disabled={saving} onClick={() => saveEditedText(note.id)} className="px-3 py-1.5 rounded-lg bg-slate-900 text-white text-sm disabled:opacity-50">
                        حفظ النص
                      </button>
                    </div>
                  ) : (
                    <p className="whitespace-pre-wrap text-slate-800">{note.text}</p>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(note.images || []).map((image) => (
                      <div key={image.id} className="relative aspect-square rounded-xl overflow-hidden border bg-slate-50">
                        {image.url ? (
                          <img src={image.url} alt={image.originalFileName || "صورة مرجعية"} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs text-slate-400 p-2 block">الصورة غير متاحة</span>
                        )}
                        <div className="absolute inset-x-1 bottom-1 flex justify-between">
                          <button type="button" className="bg-white/90 rounded-full p-1" onClick={() => image.url && setPreview({ url: image.url, title: "معاينة الصورة" })} aria-label="معاينة الصورة">
                            <ZoomIn size={14} />
                          </button>
                          <button type="button" disabled={saving} className="bg-white/90 text-red-600 rounded-full p-1" onClick={() => removeSavedImage(image.id)} aria-label="حذف الصورة">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <label className="inline-flex items-center gap-1 text-sm font-bold text-purple-700 cursor-pointer">
                    <ImagePlus size={15} />
                    إضافة صور
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                      multiple
                      className="hidden"
                      disabled={saving}
                      onChange={(event) => {
                        uploadToNote(note.id, event.target.files);
                        event.target.value = "";
                      }}
                    />
                  </label>
                </article>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <label className="block">
              <span className="font-bold text-slate-800">وصف التغييرات</span>
              <textarea
                value={text}
                onChange={(event) => setText(event.target.value)}
                rows={7}
                disabled={saving}
                placeholder="اكتب تعليمات الطباعة أو التعديل المطلوب"
                className="mt-2 w-full rounded-2xl border border-slate-200 px-3 py-3 text-base focus:outline-none focus:ring-4 focus:ring-purple-200 focus:border-purple-400 disabled:bg-slate-50"
              />
            </label>

            <div>
              <div className="flex items-center justify-between">
                <span className="font-bold text-slate-800">صور مرجعية للتغييرات</span>
                <span className="text-xs text-slate-400">اختياري</span>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => inputRef.current?.click()}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  addPending(event.dataTransfer.files);
                }}
                className="mt-2 w-full min-h-28 rounded-2xl border-2 border-dashed border-purple-200 bg-purple-50 text-purple-800 font-bold flex flex-col items-center justify-center gap-1 disabled:opacity-50"
              >
                <ImagePlus size={20} />
                إضافة صور
                <span className="text-xs font-medium text-purple-500">JPG, PNG, WebP — حتى 8 صور، 8MB للصورة</span>
              </button>
              <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp"
                multiple
                className="hidden"
                onChange={(event) => {
                  addPending(event.target.files);
                  event.target.value = "";
                }}
              />
              {pending.length > 0 && (
                <div className="grid grid-cols-3 gap-2 mt-3">
                  {pending.map((item) => (
                    <div key={item.id} className="relative aspect-square rounded-xl overflow-hidden border">
                      <img src={item.url} alt={item.file.name} className="w-full h-full object-cover" />
                      <span className="absolute top-1 right-1 text-[10px] bg-amber-400 text-amber-950 rounded px-1 font-bold">جديدة</span>
                      <div className="absolute inset-x-1 bottom-1 flex justify-between">
                        <button type="button" className="bg-white/90 rounded-full p-1" onClick={() => setPreview({ url: item.url, title: "معاينة الصورة" })} aria-label="معاينة الصورة">
                          <ZoomIn size={14} />
                        </button>
                        <button type="button" className="bg-white/90 text-red-600 rounded-full p-1" onClick={() => removePending(item.id)} aria-label="إزالة">
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {formError && <p className="text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{formError}</p>}
          {saving && pending.length > 0 && (
            <div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-purple-600 transition-all" style={{ width: `${progress}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-1">جاري الحفظ {progress ? `${progress}%` : ""}</p>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t px-4 sm:px-6 py-3 flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-full sm:w-auto px-6 py-3 rounded-2xl bg-purple-600 hover:bg-purple-700 text-white font-bold disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {saving && <Loader2 size={18} className="animate-spin" />}
            حفظ التغييرات
          </button>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[100] bg-black/80 flex flex-col items-center justify-center p-4" onClick={() => setPreview(null)}>
          <p className="text-white mb-3 font-bold">{preview.title || "معاينة الصورة"}</p>
          <img src={preview.url} alt="معاينة الصورة" className="max-w-full max-h-[80vh] rounded-xl" />
        </div>
      )}
    </div>,
    document.body
  );
};

export default OrderChangesModal;
